import { Provider } from 'mobx-react';
import { rootStoreHandle } from '@/mobx';
import Head from 'next/head';
import Layout from '@/components/Layout';
import dynamic from 'next/dynamic';
import CampaignWrap from '@/components/activity/CampaignWrap';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { cache, deepCloneAssign, getUserAccount, isBroswer, isEmpty } from '@/utils';
import PageErrorBoundary from '@/components/ErrorBoundary/PageErrorBoundary';
import MonitorCore from '@insta/monitor-core';
import { Arms } from '@/components/Layout/ScriptSnippets';
import { ProtectionJurisdictions } from '@/components/Layout/CookieBot/CookieConsent.config';

const PageServerError = dynamic(() => import('@/components/ErrorPage/PageServerError'), { ssr: false });
const PageNotAvailable = dynamic(() => import('@/components/ErrorPage/PageNotAvailable'), { ssr: false });

require('../../src/styles/base.less');
/**
 * @description 初始化Mobx状态
 * @param {object} pageData 服务端响应的数据
 * @param {object} pageProps getServerSideProps
 * @return {object} initial state
 */
function handleInitialState(pageData, pageProps) {
  try {
    const { commonState, appInject, runtimeInject } = pageData || {};
    const baseState = {
      app: {
        isMobile: runtimeInject.isMobile,
        isIOS: runtimeInject.isIOS,
        isAndroid: runtimeInject.isAndroid,
        isApp: runtimeInject.isApp,
        webp: runtimeInject.webp,
        insrc: runtimeInject.insrc,
        PodEnv: appInject.PodEnv,
        storeUrlExtend: appInject.storeUrlExtend,
        isCN: appInject.isCN,
        isProd: appInject.isProd,
        isDev: appInject.isDev,
        isLocal: appInject.isLocal,
        isSit: appInject.isSit,
        isTest: appInject.isTest,
        originalUrl: runtimeInject.originalUrl,
        urlPathName: runtimeInject.urlPathName,
        urlFull: runtimeInject.urlFull,
        urlQuery: runtimeInject.urlQuery,
        urlParams: runtimeInject.urlParams,
        isCloudUpgradeOrderConfirmPage: runtimeInject.isCloudUpgradeOrderConfirmPage,
        isOrderConfirmPage: runtimeInject.isOrderConfirmPage,
        isOrderPayPage: runtimeInject.isOrderPayPage,
        isOrderResultPage: runtimeInject.isOrderResultPage,
        isSubscriptionPage: runtimeInject.isSubscriptionPage,
        isCartPage: runtimeInject.isCartPage,
        isProductPage: runtimeInject.isProductPage,
        G_PageMenusFixed: runtimeInject.pageMenusFixed,
        G_IsProductPage: runtimeInject.isProductPage,
        G_IsHomePage: runtimeInject.isHomePage,
        G_IsResellerIntro: runtimeInject.isResellerIntro,
        G_HideHeaderFooter: runtimeInject.hideHeaderFooter,
        G_HideFooterSubscribe: runtimeInject.hideSubscribe || runtimeInject?.isNotAvailable,
        // 初始化活动
        campaignWrap: new CampaignWrap({
          isProd: appInject.isProd,
          country: runtimeInject.country,
          LANG: runtimeInject.localeData,
          insrc: runtimeInject.insrc,
          urlQuery: runtimeInject.urlQuery,
          pageCommonConfig: commonState?.app?.pageCommonConfig ?? {},
          productDetail: pageProps?.initialState?.product?.productDetail, // 产品页把产品信息也透传进去做判断
          curSelectCommodityId: pageProps?.initialState?.product?.curSelectCommodityId,
          abTestData: !isEmpty(runtimeInject?.abTestData) ? runtimeInject?.abTestData : [],
          isGrayTest: runtimeInject?.isGrayTest ?? false,
        }),
        // AB实验资源
        abTestData: !isEmpty(runtimeInject?.abTestData) ? runtimeInject?.abTestData : [],
        // universalCollect 资源
        universalCollect: !isEmpty(runtimeInject?.universalCollect) ? runtimeInject?.universalCollect : {},
      },
      area: {
        LANG: runtimeInject.localeData,
        country: runtimeInject.country,
      },
      abtest: {
        abTestData: !isEmpty(runtimeInject?.abTestData) ? runtimeInject?.abTestData : [],
        universalCollect: !isEmpty(runtimeInject?.universalCollect) ? runtimeInject?.universalCollect : {},
      },
    };
    let state = deepCloneAssign(baseState, pageProps?.initialState ?? {});
    state = deepCloneAssign(state, commonState);

    // 如果刷新之后正常显示，则删除refresh信息
    if (cache.getSessionItem('_app_error_refresh')) {
      cache.removeSessionItem('_app_error_refresh');
      armsErrorSend('_app.js reload OK', {
        filename: '_app.js',
        data: { hasRefresh: cache.getSessionItem('_app_error_refresh'), ...pageData },
      });
    }

    return state;
  } catch (e) {
    if (typeof window !== 'undefined') {
      armsErrorSend(e, {
        filename: '_app.js',
        data: { hasRefresh: cache.getSessionItem('_app_error_refresh'), ...pageData },
      });

      if (cache.getSessionItem('_app_error_refresh')) {
        cache.removeSessionItem('_app_error_refresh');
      } else if (isEmpty(pageData?.runtimeInject)) {
        armsErrorSend(e, {
          filename: '_app.js_reload',
          data: { hasRefresh: cache.getSessionItem('_app_error_refresh'), ...pageData },
        });
        cache.setSessionItem('_app_error_refresh', 1);
        window.location.reload();
      }
    }
  }
}

const SafeHydrate = (props) => {
  const { Component, pageProps, pageData } = props;
  const { enableSSR } = pageData.runtimeInject ?? {};
  // 客户端渲染
  if (process.browser) {
    return <Component {...pageProps} />;
  }
  // 开启ssr，服务端渲染
  if (!process.browser && enableSSR) {
    return <Component {...pageProps} />;
  }
  // 开启ssr配置，服务端不渲染
  if (!process.browser && !enableSSR) {
    return null;
  }
};

const initGAConfig = (props) => {
  const { gtag } = window;
  const { GAConfig = {}, runtimeInject = {} } = props?.pageData || {};
  const { country, abTestData = [] } = runtimeInject;
  const userAccountId = getUserAccount('id');
  const gtagConfig = {};
  const userProperties = {
    insta_region_code: country.code,
  };

  if (userAccountId) {
    // 用户自定义维度
    // 若上报的用户属性为字符串类型 => up.xxx
    // 若上报的用户属性为数字类型 => upn.xxx
    userProperties.user_account_id = userAccountId;
    // GA4报表默认user_id属性
    gtagConfig.user_id = userAccountId;
  }

  if (!isEmpty(abTestData)) {
    const firstExperiment = abTestData.filter((exp) => !exp?.noExperiment)[0] || abTestData[0];
    if (firstExperiment && firstExperiment?.visitorId !== '') {
      // 页面开启AB实验且有实验数据时，带上自定义维度进行GA上报
      const { visitorId, flagName, targetingResult } = firstExperiment;
      userProperties.flagship_visitor_id = visitorId;
      userProperties.flagship_experiment_name = flagName;
      userProperties.flagship_experiment_grp = targetingResult;
      // 将abTestData存入localStorage，用于在结账页带上实验组上报GA
      const abTestDataStr = JSON.stringify(firstExperiment);
      localStorage.setItem('abTestData', abTestDataStr);
    }
  } else {
    const abTestDataStr = localStorage.getItem('abTestData');
    if (abTestDataStr) {
      try {
        const { visitorId, flagName, targetingResult } = JSON.parse(abTestDataStr);
        userProperties.flagship_visitor_id = visitorId;
        userProperties.flagship_experiment_name = flagName;
        userProperties.flagship_experiment_grp = targetingResult;
      } catch (err) {
        console.log(err);
      }
    }
  }

  gtag('set', 'user_properties', userProperties);
  gtag('config', GAConfig.ga4_id, gtagConfig);
  gtag('config', GAConfig.ga4_dataSet_id, gtagConfig);
};

const App = (props) => {
  const { runtimeInject = {}, commonState = {}, appInject = {} } = props.pageData || {};

  const initialState = handleInitialState(props.pageData, props.pageProps);
  const rootStore = rootStoreHandle({ initialState });
  const meta = commonState?.meta?.metaInfo;
  const isError = runtimeInject.serverError || (isEmpty(runtimeInject) && isEmpty(appInject));
  MonitorCore.init();

  if (isBroswer && !isError) {
    initGAConfig(props);
    // 非GDPR地区直接加载监控
    if (!ProtectionJurisdictions.includes(runtimeInject.country.code)) Arms({ country: runtimeInject.country });
  }

  // 如果不需要Layout组件，div包裹
  const WrapperComponent = runtimeInject.isNoLayout ? ({ children }) => <div>{children}</div> : Layout;

  return (
    <PageErrorBoundary>
      <Provider {...rootStore}>
        <Head>
          <title>{meta?.title}</title>
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1.0, user-scalable=no, maximum-scale=1.0, viewport-fit=cover, shrink-to-fit=no'
          />
        </Head>
        {isError && <PageServerError info={`_app isError ${isError}`} />}
        {!isError && (
          <WrapperComponent>
            {runtimeInject.isNotAvailable && <PageNotAvailable />}
            {!runtimeInject.isNotAvailable && (
              <div suppressHydrationWarning>
                <SafeHydrate {...props} />
              </div>
            )}
          </WrapperComponent>
        )}
      </Provider>
    </PageErrorBoundary>
  );
};

App.getInitialProps = async ({ ctx }) => {
  return {
    pageData: ctx?.res?.pageData || {},
  };
};

export default App;
